import { SyntheticEvent, useCallback, EventHandler } from 'react';
import { useTrack } from './useTrack';

export const useTrackSyntheticEvent = <
  T extends Element,
  E extends SyntheticEvent<T>,
>(
  eventName?: string,
  callback?: EventHandler<E>,
  dependencyArray?: any[],
) => {
  const track = useTrack();

  return useCallback(
    (e: E) => {
      const elmt = e.currentTarget;
      const trackingName = elmt.getAttribute('data-tracking-name');
      let name = trackingName || elmt.id;
      let label = elmt.textContent;

      if (
        elmt instanceof HTMLInputElement ||
        elmt instanceof HTMLTextAreaElement
      ) {
        if (!name) {
          name = elmt.name;
        }
        label = label || elmt.value;
        if (
          elmt instanceof HTMLInputElement &&
          ['checkbox', 'radio'].includes(elmt.type)
        ) {
          label = `${elmt.checked}`;
        }
      }
      if (!name && elmt instanceof HTMLButtonElement) {
        name = elmt.type || 'Button';
      }

      if (!label && elmt.parentNode?.textContent) {
        label = elmt.parentNode.textContent;
      }

      if (!name || name.startsWith(':')) {
        if (callback) {
          return callback(e);
        }
      }

      track(eventName ? `${eventName} ${name}` : name, {
        eventName: eventName,
        label: label || undefined,
      });
      if (callback) {
        return callback(e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...(dependencyArray || []), track, eventName],
  );
};

const createUseLogEvent =
  (eventName?: string) =>
  <T extends Element, E extends SyntheticEvent<T>>(
    callback?: EventHandler<E>,
    dependencyArray?: any[],
  ) =>
    useTrackSyntheticEvent<T, E>(eventName, callback, dependencyArray);

export const useTrackSelect = createUseLogEvent('Selected');
export const useTrackChange = createUseLogEvent('Changed');
export const useTrackClick = createUseLogEvent('Clicked');
